<template>
  <div class="position-relative w-100 h-100 overflow-hidden">
    <div ref="mapContainer" class="w-100 h-100" aria-hidden="true"></div>

    <!-- Loading indicator -->
    <div
      v-if="isLoading"
      class="position-absolute top-50 start-50 translate-middle"
      style="z-index: 1001"
    >
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading map...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import mapGeoJson from "@/assets/map.geojson";
import routesGeoJson from "@/assets/routes.geojson";
import {
  IslandHeaders,
  CulturalRegionHeaders,
  MigrationRouteHeaders
} from "@/types/index.ts";
import { initializeMap, addResetControl } from "@/helpers/mapboxSetup.js";
import {
  addIslandMarkers,
  addCulturalRegions,
  addMigrationWaveLayers,
  updateLayerVisibility
} from "@/helpers/mapboxSourceManager.js";
import {
  calculateMapBounds,
  fitMapToInitialBounds,
  zoomToFeature,
  resetMapView
} from "@/helpers/mapboxZoom.js";
import { Tooltip } from "bootstrap";

export default {
  name: "PacificMap",
  data() {
    return {
      map: null,
      hoverHandlerCleanup: null,
      isLoading: true,
      resetButton: null,
      resetButtonTooltip: null,
      isAutomaticZoom: false,
      isTooltipShowing: false,
      zoomEndListener: null
    };
  },
  computed: {
    ...mapGetters({
      islands: "getIslands",
      culturalRegions: "getCulturalRegions",
      migrationRoutes: "getMigrationRoutes",
      activeLayers: "activeLayers",
      selectedItem: "selectedItem",
      mapSettings: "mapSettings"
    })
  },
  async mounted() {
    await this.initializeMap();
    this.isLoading = false;
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    ...mapActions(["selectItem", "setInitialBounds"]),

    /**
     * Initialize the Mapbox map instance and setup all layers.
     *
     * Lives in component (not helper) because:
     * - Needs access to Vue $refs for DOM mounting
     * - Must store map instance in component data
     * - Requires component environment variables (VITE_MAPBOX_TOKEN)
     * - Binds onMapLoad callback that references other component methods
     */
    async initializeMap() {
      const { map } = await initializeMap(this.$refs.mapContainer, {
        accessToken:
          import.meta.env.VITE_MAPBOX_TOKEN || "your-mapbox-token-here",
        center: [170, -10],
        zoom: 4,
        minZoom: 2,
        maxZoom: 12,
        onLoad: this.onMapLoad
      });

      this.map = map;
    },

    /**
     * Callback invoked when the map finishes loading its base style.
     *
     * Lives in component (not helper) because:
     * - Acts as Vue lifecycle coordination point between map load and layer setup
     * - Orchestrates high-level sequence: calculate bounds, fit map, add layers
     * - Needs access to component methods and Vuex actions
     */
    async onMapLoad() {
      // Calculate initial bounds from all map.json features
      const initialBounds = calculateMapBounds(mapGeoJson);
      if (initialBounds) {
        this.setInitialBounds(initialBounds);
        fitMapToInitialBounds(this.map, initialBounds);
      }

      // Add reset control and store button reference
      const { button } = await addResetControl(
        this.map,
        this.handleResetMap,
        "bottom-left"
      );
      this.resetButton = button;

      // Initialize Bootstrap tooltip on the reset button
      if (this.resetButton) {
        this.resetButtonTooltip = new Tooltip(this.resetButton, {
          title: "Click this to zoom back out",
          placement: "right",
          trigger: "manual",
          animation: true
        });
      }

      // Listen for map moveend events to detect when zoom completes
      this.zoomEndListener = () => {
        if (this.isAutomaticZoom) {
          // Show tooltip after automatic zoom completes
          this.$nextTick(() => {
            if (this.resetButtonTooltip) {
              this.resetButtonTooltip.show();
              this.isTooltipShowing = true;
            }
          });
          this.isAutomaticZoom = false;
        } else if (this.isTooltipShowing) {
          // If tooltip is showing and user manually zooms, hide it
          if (this.resetButtonTooltip) {
            this.resetButtonTooltip.hide();
            this.isTooltipShowing = false;
          }
        }
      };
      this.map.on("moveend", this.zoomEndListener);

      this.addAllLayers();

      // Apply initial layer visibility state
      updateLayerVisibility(this.map, this.activeLayers);
    },

    /**
     * Wire up all map layers with component state and actions.
     *
     * Lives in component (not helper) because:
     * - Wires Vuex state (islands, culturalRegions, migrationRoutes) to layers
     * - Passes Vuex action (selectItem) as callback for user interactions
     * - Manages cleanup handler reference in component data
     */
    addAllLayers() {
      addCulturalRegions(
        this.map,
        mapGeoJson,
        this.culturalRegions,
        CulturalRegionHeaders.ID,
        this.selectItem
      );

      // Add migration wave 1 layers (routes, arrows, labels, polygons)
      addMigrationWaveLayers(
        this.map,
        routesGeoJson,
        mapGeoJson,
        "migration_wave_1",
        "1",
        "#ffa500",
        this.migrationRoutes,
        MigrationRouteHeaders.ID,
        this.selectItem
      );

      // Add migration wave 2 layers (routes, arrows, labels, polygons)
      addMigrationWaveLayers(
        this.map,
        routesGeoJson,
        mapGeoJson,
        "migration_wave_2",
        "2",
        "#ff6b6b",
        this.migrationRoutes,
        MigrationRouteHeaders.ID,
        this.selectItem
      );

      // Add island markers last so they sit on top of other layers
      this.hoverHandlerCleanup = addIslandMarkers(
        this.map,
        mapGeoJson,
        this.islands,
        IslandHeaders.ID,
        this.selectItem
      );
    },

    /**
     * Clean up map resources before component is destroyed.
     *
     * Lives in component (not helper) because:
     * - Ties directly to Vue lifecycle hook (beforeUnmount)
     * - Manages component-owned cleanup handler and map instance
     * - Ensures proper resource disposal tied to component lifecycle
     */
    cleanup() {
      if (this.hoverHandlerCleanup) {
        this.hoverHandlerCleanup.cleanup();
      }
      if (this.zoomEndListener && this.map) {
        this.map.off("moveend", this.zoomEndListener);
      }
      if (this.resetButtonTooltip) {
        this.resetButtonTooltip.dispose();
        this.resetButtonTooltip = null;
      }
      if (this.map) {
        this.map.remove();
      }
    },

    /**
     * Zoom to currently selected item.
     *
     * Lives in component (not helper) because:
     * - Needs access to Vuex state (selectedItem)
     * - Needs to find the matching GeoJSON feature from map.json or routes.json
     * - Calls helper function with found feature
     */
    zoomToCurrentSelection() {
      if (!this.map || !this.selectedItem) return;

      const id =
        this.selectedItem.mapbox_id ||
        this.selectedItem[IslandHeaders.ID] ||
        this.selectedItem[CulturalRegionHeaders.ID] ||
        this.selectedItem[MigrationRouteHeaders.ID];

      const feature =
        mapGeoJson.features.find((f) => f.properties.mapbox_id === id) ||
        routesGeoJson?.features.find(
          (f) => f.properties?.mapbox_id === id || f.properties?.id === id
        );

      if (feature) {
        // Mark this as an automatic zoom (not manual)
        this.isAutomaticZoom = true;
        zoomToFeature(this.map, feature);
      } else {
        console.error("Feature not found for zoom:", {
          id,
          selectedItem: this.selectedItem
        });
      }
    },

    /**
     * Reset map to initial view.
     *
     * Lives in component (not helper) because:
     * - Needs access to Vuex state (mapSettings.initialBounds)
     * - Provides callback for reset button component
     */
    handleResetMap() {
      if (!this.map || !this.mapSettings.initialBounds) return;

      // Hide tooltip when home button is clicked
      if (this.resetButtonTooltip && this.isTooltipShowing) {
        this.resetButtonTooltip.hide();
        this.isTooltipShowing = false;
      }

      // Mark this as manual zoom (not automatic)
      this.isAutomaticZoom = false;

      resetMapView(this.map, this.mapSettings.initialBounds);
    }
  },
  watch: {
    /**
     * Reactively update island markers when island data changes.
     *
     * Lives in component (not helper) because:
     * - Responds to Vue reactive state changes from Vuex store
     * - Re-wires component state/actions/closures to new layer instance
     */
    islands: {
      handler() {
        if (!this.map) return;
        this.hoverHandlerCleanup = addIslandMarkers(
          this.map,
          mapGeoJson,
          this.islands,
          IslandHeaders.ID,
          this.selectItem
        );
      },
      deep: true
    },

    /**
     * Reactively update cultural region layers when region data changes.
     *
     * Lives in component (not helper) because:
     * - Responds to Vue reactive state changes from Vuex store
     * - Re-wires component state/actions to new layer instance
     */
    culturalRegions: {
      handler() {
        if (!this.map) return;
        addCulturalRegions(
          this.map,
          mapGeoJson,
          this.culturalRegions,
          CulturalRegionHeaders.ID,
          this.selectItem
        );
      },
      deep: true
    },

    /**
     * Reactively toggle layer visibility when user changes active layers.
     *
     * Lives in component (not helper) because:
     * - Responds to Vue reactive state changes from Vuex store
     * - Bridges user interaction state to map display logic
     */
    activeLayers: {
      handler() {
        if (!this.map) return;
        updateLayerVisibility(this.map, this.activeLayers);
      },
      deep: true
    },

    /**
     * Reactively zoom to selected item when selection changes (if enabled).
     *
     * Lives in component (not helper) because:
     * - Responds to Vue reactive state changes from Vuex store
     * - Checks mapSettings.zoomToSelected before triggering zoom
     * - Calls component method that accesses other Vuex state
     */
    selectedItem: {
      handler(newItem, oldItem) {
        // Only zoom if:
        // 1. Zoom to selected is enabled
        // 2. There's a new item selected
        // 3. It's different from the old item (avoid re-zooming on same selection)
        if (this.mapSettings.zoomToSelected && newItem && newItem !== oldItem) {
          this.zoomToCurrentSelection();
        }
      }
    }
  }
};
</script>

<style scoped>
/* All styling is now handled by Bootstrap utility classes */
</style>
